// Etiquetas en español para las claves estables del catálogo de ejercicios.
// Las claves coinciden con las columnas de `exercises` (primary_muscle, equipment,
// category, level, mechanic, force) y con backend/seeds/dictionaries.es.json.

export const ACTIVE_ROUTINE = {
  badge: 'Activa',
  follow: 'Marcar como activa',
  unfollow: 'Dejar de seguir',
  shortcut: (name: string) => `Tu rutina: ${name}`,
} as const;

/** Vista previa de un día de rutina, sin empezar la sesión. */
export const DAY_PREVIEW = {
  start: 'Empezar entreno',
  resume: 'Continuar entreno',
  startDay: (day: string) => `Empezar ${day}`,
  open: 'Ver el día',
  openLabel: (day: string) => `Ver ${day} sin empezarlo`,
  back: 'Volver a Entrenar',
  listHint: 'Toca un día para verlo; ▶ lo empieza directamente.',
  exercises: (count: number) => `${count} ${count === 1 ? 'ejercicio' : 'ejercicios'}`,
  empty: 'Este día no tiene ejercicios. Puedes empezarlo igualmente y añadirlos sobre la marcha.',
  notFound: 'Este día ya no existe en la rutina.',
  loadError: 'No hemos podido cargar el día',
  setN: (index: number) => `Serie ${index}`,
  lastMark: 'Última marca',
  technique: (name: string) => `Ver la técnica de ${name}`,
  techniqueHint: 'Toca un ejercicio para ver la técnica.',
  superset: (group: number) => `Superserie ${group}`,
} as const;

/** Sesión empezada sin ninguna serie: descartarla en vez de dejarla abierta. */
export const DISCARD_SESSION = {
  title: 'No has registrado ninguna serie',
  body: 'Si lo descartas, este entreno se borra y no queda en tu historial.',
  discard: 'Descartar entreno',
  keep: 'Seguir',
  discarding: 'Descartando…',
  unfinished: 'Sin terminar',
  finish: 'Terminar',
  discardShort: 'Descartar',
  error: 'No se ha podido descartar el entreno',
  finishError: 'No se ha podido terminar el entreno',
  replace: 'Descartarlo y empezar este',
} as const;

/** Las dos ayudas de esfuerzo, iguales en la sesión y en el editor de rutinas. */
export const EFFORT_HELP = {
  rpeLabel: 'Qué es el RPE',
  rpe: 'RPE: lo que te ha costado la serie, del 1 al 10. 10 es que no podías hacer ni una repetición más; 8, que te quedaban unas 2. Es opcional.',
  rirLabel: 'Qué es el RIR',
  rir: 'RIR: repeticiones que te quedan «en la recámara» al acabar la serie. 0 es llegar al fallo; 2, que podrías haber hecho 2 más.',
  effortLabel: 'Qué son el RPE y el RIR',
} as const;

export const TRAINING_TEXT = {
  sets: (count: number) => `${count} ${count === 1 ? 'serie' : 'series'}`,
  reps: (value: string | number) => `${value} ${Number(value) === 1 ? 'repe' : 'repes'}`,
  perSide: 'por lado',
  perSetReps: 'Según cada serie',
  target: (value: string) => `Objetivo: ${value}`,
  exerciseSummary: 'Resumen por ejercicio',
  rest: (seconds: number) => `${seconds} s de descanso`,
} as const;

export const MUSCLES: Record<string, string> = {
  abdominals: 'Abdominales',
  abductors: 'Abductores',
  adductors: 'Aductores',
  biceps: 'Bíceps',
  calves: 'Gemelos',
  chest: 'Pecho',
  forearms: 'Antebrazos',
  glutes: 'Glúteos',
  hamstrings: 'Femorales',
  lats: 'Dorsales',
  'lower-back': 'Lumbares',
  'middle-back': 'Espalda media',
  neck: 'Cuello',
  quadriceps: 'Cuádriceps',
  shoulders: 'Hombros',
  traps: 'Trapecios',
  triceps: 'Tríceps',
};

export const EQUIPMENT: Record<string, string> = {
  barbell: 'Barra',
  dumbbell: 'Mancuernas',
  cable: 'Polea',
  machine: 'Máquina',
  kettlebells: 'Kettlebell',
  bands: 'Bandas elásticas',
  // `body-only` solo lo puede llevar un ejercicio propio (se elige en su
  // formulario): el catálogo global guarda el peso corporal como
  // `equipment: null`, que es «Sin material». Por eso no es un filtro.
  'body-only': 'Peso corporal',
  'medicine-ball': 'Balón medicinal',
  'exercise-ball': 'Fitball',
  'e-z-curl-bar': 'Barra Z',
  'foam-roll': 'Rodillo de espuma',
  other: 'Otro',
  none: 'Sin material',
};

export const CATEGORIES: Record<string, string> = {
  strength: 'Fuerza',
  cardio: 'Cardio',
  mobility: 'Movilidad',
  stretching: 'Estiramiento',
  plyometrics: 'Pliometría',
};

export const LEVELS: Record<string, string> = {
  beginner: 'Principiante',
  intermediate: 'Intermedio',
  advanced: 'Avanzado',
};

export const MECHANICS: Record<string, string> = {
  compound: 'Multiarticular',
  isolation: 'Aislamiento',
};

export const FORCES: Record<string, string> = {
  push: 'Empuje',
  pull: 'Tracción',
  static: 'Estático',
};

export const SET_TYPES: Record<string, string> = {
  normal: 'Normal',
  warmup: 'Calentamiento',
  failure: 'Al fallo',
  drop: 'Drop set',
};

const label = (dict: Record<string, string>) => (key: string | null | undefined) =>
  key == null ? '' : (dict[key] ?? key);

export const muscleLabel = label(MUSCLES);
/** `null` en la API significa "sin material". */
export const equipmentLabel = (key: string | null | undefined) =>
  key == null ? EQUIPMENT.none : (EQUIPMENT[key] ?? key);
export const categoryLabel = label(CATEGORIES);
export const levelLabel = label(LEVELS);
export const mechanicLabel = label(MECHANICS);
export const forceLabel = label(FORCES);
export const setTypeLabel = label(SET_TYPES);

// --- Listas ordenadas para los selectores y los chips de filtro -------------

/**
 * Material que se ofrece como filtro del catálogo. `body-only` no está: el
 * catálogo global guarda los ejercicios de peso corporal con `equipment: null`
 * (los selecciona «Sin material»), así que ese chip siempre devolvía una lista
 * vacía salvo para algún ejercicio propio.
 */
export const FILTER_EQUIPMENT_KEYS = [
  'barbell',
  'dumbbell',
  'machine',
  'cable',
  'bands',
  'kettlebells',
  'e-z-curl-bar',
  'medicine-ball',
  'exercise-ball',
  'foam-roll',
  'other',
] as const;

/** Músculos en el orden en que se ofrecen; los más usados primero. */
export const MUSCLE_KEYS = [
  'chest',
  'lats',
  'middle-back',
  'shoulders',
  'biceps',
  'triceps',
  'quadriceps',
  'hamstrings',
  'glutes',
  'calves',
  'abdominals',
  'lower-back',
  'traps',
  'forearms',
  'abductors',
  'adductors',
  'neck',
] as const;

/** Material en el orden en que se ofrece. `none` no va aquí: es el `null`. */
export const EQUIPMENT_KEYS = [
  'barbell',
  'dumbbell',
  'machine',
  'cable',
  'body-only',
  'bands',
  'kettlebells',
  'e-z-curl-bar',
  'medicine-ball',
  'exercise-ball',
  'foam-roll',
  'other',
] as const;

export const CATEGORY_KEYS = [
  'strength',
  'cardio',
  'mobility',
  'stretching',
  'plyometrics',
] as const;

export const LEVEL_KEYS = ['beginner', 'intermediate', 'advanced'] as const;
export const MECHANIC_KEYS = ['compound', 'isolation'] as const;
export const FORCE_KEYS = ['push', 'pull', 'static'] as const;

/**
 * Valor que el contrato reserva en el filtro `equipment` para seleccionar los
 * ejercicios que no necesitan material (`equipment IS NULL`).
 */
export const NO_EQUIPMENT_FILTER = 'ninguno';

// --- Rutinas ---------------------------------------------------------------

export const VISIBILITY: Record<string, string> = {
  private: 'Privada',
  unlisted: 'Con enlace',
  public: 'Pública',
};

/** Qué implica cada visibilidad, en una línea, para el editor de rutinas. */
export const VISIBILITY_HINTS: Record<string, string> = {
  private: 'Solo tú puedes verla.',
  unlisted: 'Cualquiera con el enlace puede verla, pero no aparece en búsquedas.',
  public: 'Visible para toda la comunidad.',
};

export const VISIBILITY_KEYS = ['private', 'unlisted', 'public'] as const;

export const visibilityLabel = label(VISIBILITY);
